import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDotBotsAuth } from "@dotbots-boutique/auth-sdk/react";
import { useApi } from "../lib/api";
import { hasPermission } from "../lib/permissions";
import { useI18n, useT } from "../i18n/I18nProvider";
import { ConfirmDeleteButton } from "../components/shared/ConfirmDeleteButton";
import type { KnowledgeArticle } from "@shared/types";

export default function KnowledgeBase() {
  const api = useApi();
  const t = useT();
  const { lang } = useI18n();
  const { user } = useDotBotsAuth();
  const roles = user?.roles ?? [];
  // Viewers can read articles but can't write or delete them — gate the
  // create form and the row-level edit/delete actions.
  const canWrite = hasPermission(roles, "knowledge.write");
  const canDelete = hasPermission(roles, "knowledge.delete");
  const canUseAi = hasPermission(roles, "ai.use");
  const [search, setSearch] = useState("");
  const [articles, setArticles] = useState<KnowledgeArticle[]>([]);
  const [editing, setEditing] = useState<KnowledgeArticle | null>(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [tags, setTags] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  // Generator panel state. Kept inline (not a modal) so the operator
  // can keep an eye on the existing list while pasting in docs and
  // see fresh rows pop in after generation.
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [docs, setDocs] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [generatedFlash, setGeneratedFlash] = useState<{ count: number } | null>(null);

  const load = async () => {
    const res = await api.get<{ data: KnowledgeArticle[] }>(`/api/knowledge?q=${encodeURIComponent(search)}`);
    setArticles(res.data);
  };

  useEffect(() => {
    const id = setTimeout(load, 200);
    return () => clearTimeout(id);
  }, [search]);

  // ?open=<id> jumps straight into edit mode for that article — used
  // by the "create knowledge article" button on the ticket detail to
  // hand the operator an editable draft.
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || articles.length === 0 || !canWrite) return;
    const target = articles.find((a) => a.id === openId);
    if (target) {
      setEditing(target);
      setTitle(target.title);
      setContent(target.content);
      setTags((target.tags ?? []).join(", "));
      // Clear the param so a refresh doesn't keep popping the editor.
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
  }, [articles, searchParams, canWrite, setSearchParams]);

  const save = async () => {
    const payload = {
      title,
      content,
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    if (editing) {
      await api.patch(`/api/knowledge/${editing.id}`, payload);
    } else {
      await api.post("/api/knowledge", payload);
    }
    setEditing(null);
    setTitle("");
    setContent("");
    setTags("");
    await load();
  };

  const startEdit = (a: KnowledgeArticle) => {
    setEditing(a);
    setTitle(a.title);
    setContent(a.content);
    setTags((a.tags ?? []).join(", "));
  };

  const remove = async (id: string) => {
    await api.del(`/api/knowledge/${id}`);
    await load();
  };

  const generateFromDocs = async () => {
    setGenerateError(null);
    if (docs.trim().length < 40) {
      setGenerateError(t("knowledge.generate.tooShort"));
      return;
    }
    setGenerating(true);
    try {
      const res = await api.post<{ articles: { id: string; title: string }[] }>(
        "/api/knowledge/generate",
        { text: docs, language: lang },
      );
      setDocs("");
      setGeneratorOpen(false);
      setGeneratedFlash({ count: res.articles.length });
      setTimeout(() => setGeneratedFlash(null), 5000);
      await load();
    } catch (err) {
      setGenerateError((err as Error).message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <h2>{t("knowledge.title")}</h2>

      <div className="card">
        <div className="field">
          <label>{t("common.search")}</label>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("knowledge.searchPlaceholder")} />
        </div>
      </div>

      {canWrite && canUseAi && (
        <div className="card kb-generator-card">
          <div className="kb-generator-header">
            <div>
              <h3 style={{ margin: 0 }}>{t("knowledge.generate.heading")}</h3>
              <p style={{ color: "var(--text-muted)", margin: "4px 0 0", fontSize: 13 }}>
                {t("knowledge.generate.intro")}
              </p>
            </div>
            {!generatorOpen && (
              <button type="button" className="primary" onClick={() => setGeneratorOpen(true)}>
                {t("knowledge.generate.openButton")}
              </button>
            )}
          </div>

          {generatedFlash && (
            <div className="kb-generator-flash">
              {t("knowledge.generate.success", { n: generatedFlash.count })}
            </div>
          )}

          {generatorOpen && (
            <div className="kb-generator-panel">
              <div className="field">
                <label>{t("knowledge.generate.textField")}</label>
                <textarea
                  value={docs}
                  onChange={(e) => setDocs(e.target.value)}
                  rows={10}
                  placeholder={t("knowledge.generate.placeholder")}
                  disabled={generating}
                />
                <small style={{ color: "var(--text-muted)" }}>
                  {t("knowledge.generate.help")}
                </small>
              </div>
              {generateError && (
                <div style={{ color: "var(--danger)", fontSize: 13, marginBottom: 8 }}>
                  {generateError}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="primary"
                  onClick={generateFromDocs}
                  disabled={generating || docs.trim().length < 40}
                >
                  {generating ? t("knowledge.generate.generating") : t("knowledge.generate.generateButton")}
                </button>
                <button
                  type="button"
                  onClick={() => { setGeneratorOpen(false); setGenerateError(null); }}
                  disabled={generating}
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {canWrite && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>{editing ? t("knowledge.editArticle") : t("knowledge.newArticle")}</h3>
          <div className="field">
            <label>{t("knowledge.titleField")}</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="field">
            <label>{t("knowledge.contentField")}</label>
            <textarea value={content} onChange={(e) => setContent(e.target.value)} />
          </div>
          <div className="field">
            <label>{t("knowledge.tagsField")}</label>
            <input value={tags} onChange={(e) => setTags(e.target.value)} />
          </div>
          <div style={{ textAlign: "right" }}>
            {editing && <button onClick={() => { setEditing(null); setTitle(""); setContent(""); setTags(""); }}>{t("common.cancel")}</button>}{" "}
            <button className="primary" disabled={!title.trim() || !content.trim()} onClick={save}>
              {editing ? t("common.save") : t("common.add")}
            </button>
          </div>
        </div>
      )}

      <div className="card">
        {articles.map((a) => (
          <div key={a.id} style={{ paddingBottom: 12, marginBottom: 12, borderBottom: "1px solid var(--border)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>{a.title}</strong>
              <div>
                {canWrite && <button onClick={() => startEdit(a)}>{t("common.edit")}</button>}
                {canDelete && (
                  <>
                    {" "}
                    <ConfirmDeleteButton onDelete={() => remove(a.id)} hint={t("knowledge.deleteConfirm")} />
                  </>
                )}
              </div>
            </div>
            <div style={{ whiteSpace: "pre-wrap", marginTop: 6 }}>{a.content}</div>
            {a.tags && a.tags.length > 0 && (
              <div style={{ marginTop: 6, color: "var(--text-muted)", fontSize: 12 }}>
                {a.tags.map((tag) => `#${tag}`).join(" ")}
              </div>
            )}
          </div>
        ))}
        {articles.length === 0 && <p style={{ color: "var(--text-muted)" }}>{t("knowledge.empty")}</p>}
      </div>
    </>
  );
}
